import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from "react";

import {
  type Rotation,
  rotateDelta,
  rotatePoint,
  toCssPercent,
} from "../components/rotationUtils";

const DRAG_THRESHOLD_PX = 3;

/** One arrow key, as a fraction of the artwork. Big enough to clear a line of type. */
export const SOURCE_PIN_KEY_STEP = 0.05;

const KEY_DISPLAY_DELTA: Record<string, { dx: number; dy: number }> = {
  ArrowLeft: { dx: -SOURCE_PIN_KEY_STEP, dy: 0 },
  ArrowRight: { dx: SOURCE_PIN_KEY_STEP, dy: 0 },
  ArrowUp: { dx: 0, dy: -SOURCE_PIN_KEY_STEP },
  ArrowDown: { dx: 0, dy: SOURCE_PIN_KEY_STEP },
};

export type SourcePinPoint = { relX: number; relY: number };

function clampUnit(n: number): number {
  return Math.min(1, Math.max(0, n));
}

/**
 * Where an arrow key drops the pin. Arrows are screen directions; the stored
 * point is natural, so a rotated page turns the nudge before it is written.
 * A key that is not an arrow leaves the pin where it is.
 */
export function sourcePinPointFromKey(
  start: SourcePinPoint,
  key: string,
  rotation: Rotation,
): SourcePinPoint | null {
  const display = KEY_DISPLAY_DELTA[key];
  if (!display) return null;
  // why: screen arrows are not natural dx/dy on a rotated page, so the nudge is turned before it is stored.
  return commitSourcePinMove(start, rotateDelta(display, rotation));
}

/**
 * The position to store after a drag, in the same 0–1 space the pin was
 * stored in. A pin that has not been through this write keeps its stored point.
 */
export function commitSourcePinMove(
  start: SourcePinPoint,
  naturalDelta: { dx: number; dy: number },
): SourcePinPoint {
  return {
    relX: clampUnit(start.relX + naturalDelta.dx),
    relY: clampUnit(start.relY + naturalDelta.dy),
  };
}

function storedKey(
  stored: (SourcePinPoint & { page?: number }) | null,
): string {
  if (!stored) return "";
  return `${stored.page ?? ""}:${stored.relX}:${stored.relY}`;
}

/**
 * Draws `stored` until `commit` writes a drag. A later stored pin (a different
 * page or a different point) does not inherit that write.
 */
export function useCommittedSourcePin<
  T extends SourcePinPoint & { page?: number },
>(
  stored: T,
): {
  drawn: T;
  commit: (next: SourcePinPoint) => void;
};
export function useCommittedSourcePin<
  T extends SourcePinPoint & { page?: number },
>(
  stored: T | null,
): {
  drawn: T | null;
  commit: (next: SourcePinPoint) => void;
};
export function useCommittedSourcePin<
  T extends SourcePinPoint & { page?: number },
>(
  stored: T | null,
): {
  drawn: T | null;
  commit: (next: SourcePinPoint) => void;
} {
  const key = storedKey(stored);
  const [committed, setCommitted] = useState<{
    key: string;
    relX: number;
    relY: number;
  } | null>(null);

  const drawn =
    stored == null
      ? null
      : committed != null && committed.key === key
        ? { ...stored, relX: committed.relX, relY: committed.relY }
        : stored;

  const commit = useCallback(
    (next: SourcePinPoint) => {
      setCommitted({ key, relX: next.relX, relY: next.relY });
    },
    [key],
  );

  return { drawn, commit };
}

/**
 * Pointer drag for one source pin. The pin's parent is the box it is
 * positioned against. The dropped point is only reported through `onCommit`
 * — a move under the threshold leaves the stored point alone.
 */
export function useSourcePinGesture(args: {
  relX: number;
  relY: number;
  rotation: Rotation;
  onCommit: (next: SourcePinPoint) => void;
}): {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
} {
  const argsRef = useRef(args);
  argsRef.current = args;
  const startRef = useRef<{
    px: number;
    py: number;
    relX: number;
    relY: number;
    rotation: Rotation;
    box: HTMLElement;
    node: HTMLElement;
  } | null>(null);

  const preview = (
    node: HTMLElement,
    relX: number,
    relY: number,
    rotation: Rotation,
  ) => {
    const rotated = rotatePoint({ x: relX, y: relY }, rotation);
    node.style.left = toCssPercent(rotated.x);
    node.style.top = toCssPercent(rotated.y);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    event.stopPropagation();
    const node = event.currentTarget;
    const box = node.parentElement;
    if (!box) return;
    node.setPointerCapture?.(event.pointerId);
    const current = argsRef.current;
    startRef.current = {
      px: event.clientX,
      py: event.clientY,
      relX: current.relX,
      relY: current.relY,
      rotation: current.rotation,
      box,
      node,
    };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const start = startRef.current;
    if (!start) return;
    event.stopPropagation();
    const rect = start.box.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const natural = rotateDelta(
      {
        dx: (event.clientX - start.px) / rect.width,
        dy: (event.clientY - start.py) / rect.height,
      },
      start.rotation,
    );
    const next = commitSourcePinMove(start, natural);
    preview(start.node, next.relX, next.relY, start.rotation);
  };

  const finish = (event: ReactPointerEvent<HTMLElement>, commit: boolean) => {
    const start = startRef.current;
    startRef.current = null;
    if (!start) return;
    event.stopPropagation();
    const rect = start.box.getBoundingClientRect();
    const travel =
      Math.abs(event.clientX - start.px) + Math.abs(event.clientY - start.py);
    if (
      !commit ||
      travel <= DRAG_THRESHOLD_PX ||
      rect.width <= 0 ||
      rect.height <= 0
    ) {
      preview(start.node, start.relX, start.relY, start.rotation);
      return;
    }
    const natural = rotateDelta(
      {
        dx: (event.clientX - start.px) / rect.width,
        dy: (event.clientY - start.py) / rect.height,
      },
      start.rotation,
    );
    argsRef.current.onCommit(commitSourcePinMove(start, natural));
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    finish(event, true);
  };

  const onPointerCancel = (event: ReactPointerEvent<HTMLElement>) => {
    finish(event, false);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    const current = argsRef.current;
    const next = sourcePinPointFromKey(
      { relX: current.relX, relY: current.relY },
      event.key,
      current.rotation,
    );
    if (!next) return;
    event.preventDefault();
    event.stopPropagation();
    current.onCommit(next);
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onKeyDown,
  };
}
